import CoreUtils from '../../utils/coreUtils.js';
import Utils3D from '../utils/utils3D.js';
import Utils from './utils.js';
import { PARAM_TYPE } from './connector.js';

export default class CachedResult
{
	constructor(pValue)
	{
		this.value = null;

		// Meshes are passed in as arrays of meshes
		let testValue = pValue;
		if(Array.isArray(pValue) && pValue.length > 0)
			testValue = pValue[0];

		this.resultType = Utils.getObjectParameterType(testValue);

		// We generally can just store most types verbatim, except for a few,
		// such as meshes, that require special handling.
		// TODO: This currently doesn't support instances, and makes a new mesh
		// for every instance instead, slowing everything down massively.
		if(this.resultType === PARAM_TYPE.GEOMETRY)
		{
			this.value = [];
			for(let pMesh of pValue)
			{
				if(Utils.getIsMeshObject(pMesh) && pMesh.isDisposed())
					throw new Error(`Caching a mesh that is already disposed ${pMesh.name}`);

				let positions = pMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind, false, true);
				let normals = pMesh.getVerticesData(BABYLON.VertexBuffer.NormalKind, false, true);
				let colors = pMesh.getVerticesData(BABYLON.VertexBuffer.ColorKind, false, true);
				let uvs = pMesh.getVerticesData(BABYLON.VertexBuffer.UVKind, false, true);

				let indices = pMesh.getIndices(false, true);

				this.value.push({
					positions: positions ? positions.slice() : null,
					normals: normals ? normals.slice() : null,
					colors: colors ? colors.slice() : null,
					uvs: uvs ? uvs.slice() : null,
					indices: indices ? indices.slice() : null,
					material: pMesh.material,
					position: pMesh.position.clone(),
					rotation: pMesh.rotation.clone(),
					scaling: pMesh.scaling.clone(),
					pivotMatrix: pMesh.getPivotMatrix().clone(),
					origRotationQuaternion: pMesh.origRotationQuaternion ? pMesh.origRotationQuaternion.clone() : null,
					parentGraphId: pMesh.parentGraphId,
					parentNodeId: pMesh.parentNodeId,
					parentMaterialNodeId: pMesh.parentMaterialNodeId,
					parentMaterialTweaksNodeId: pMesh.parentMaterialTweaksNodeId,
					receiveShadows: pMesh.receiveShadows,
					origMeshName: pMesh.name ? pMesh.name.slice(0) : 'unnamed',
					extraModelInfo: pMesh.extraModelInfo ? CoreUtils.clone(pMesh.extraModelInfo) : null,
					modsyMeshType: pMesh.modsyMeshType,
					modsyMeshSubtype: pMesh.modsyMeshSubtype,
					modsyAttachedObjectId: pMesh.modsyAttachedObjectId,
					alwaysSelectAsActiveMesh: pMesh.alwaysSelectAsActiveMesh,
					overrideMaterialSideOrientation: pMesh.overrideMaterialSideOrientation,
				});
			}
		}
		else if(pValue && pValue.clone)
			this.value = pValue.clone();
		else if(pValue)
			this.value = CoreUtils.clone(pValue, null, true);
	}

	toValue(scene, perMeshCallback = null)
	{
		let pResult = null;
		if(this.resultType === PARAM_TYPE.GEOMETRY)
		{
			pResult = [];
			for(let pSrcMesh of this.value)
			{
				let pMeshCopy = new BABYLON.Mesh(CoreUtils.createGuid(), scene);
				pMeshCopy.name = pSrcMesh.origMeshName;
				pMeshCopy.material = pSrcMesh.material;
				pMeshCopy.position = pSrcMesh.position.clone();
				pMeshCopy.rotation = pSrcMesh.rotation.clone();
				pMeshCopy.scaling = pSrcMesh.scaling.clone();
				pMeshCopy.setPivotMatrix(pSrcMesh.pivotMatrix.clone());

				pMeshCopy.origRotationQuaternion = pSrcMesh.origRotationQuaternion ? pSrcMesh.origRotationQuaternion.clone() : null;
				pMeshCopy.parentGraphId = pSrcMesh.parentGraphId;
				pMeshCopy.parentNodeId = pSrcMesh.parentNodeId;
				pMeshCopy.parentMaterialNodeId = pSrcMesh.parentMaterialNodeId;
				pMeshCopy.parentMaterialTweaksNodeId = pSrcMesh.parentMaterialTweaksNodeId;
				pMeshCopy.receiveShadows = pSrcMesh.receiveShadows;

				pMeshCopy.modsyMeshType = pSrcMesh.modsyMeshType;
				pMeshCopy.modsyMeshSubtype = pSrcMesh.modsyMeshSubtype;
				pMeshCopy.modsyAttachedObjectId = pSrcMesh.modsyAttachedObjectId;
				pMeshCopy.extraModelInfo = pSrcMesh.extraModelInfo ? CoreUtils.clone(pSrcMesh.extraModelInfo) : null;
				pMeshCopy.alwaysSelectAsActiveMesh = pSrcMesh.alwaysSelectAsActiveMesh;
				pMeshCopy.overrideMaterialSideOrientation = pSrcMesh.overrideMaterialSideOrientation;

				if(pSrcMesh.positions)
				{
					let vertexData = new BABYLON.VertexData();

					vertexData.positions = pSrcMesh.positions.slice();
					vertexData.indices = pSrcMesh.indices ? pSrcMesh.indices.slice() : null;
					vertexData.normals = pSrcMesh.normals ? pSrcMesh.normals.slice() : null;
					vertexData.uvs = pSrcMesh.uvs ? pSrcMesh.uvs.slice() : null;
					vertexData.colors = pSrcMesh.colors ? pSrcMesh.colors.slice() : null;

					vertexData.applyToMesh(pMeshCopy, true);
				}

				pMeshCopy.setEnabled(false);
				pResult.push(pMeshCopy);

				if(perMeshCallback)
					perMeshCallback(pMeshCopy);
			}
		}
		else if(this.resultType === PARAM_TYPE.BBOX)
		{
			pResult = {};
			if(this.value.minimumWorld)
			{
				pResult['minimumWorld'] = new BABYLON.Vector3(this.value.minimumWorld.x, this.value.minimumWorld.y, this.value.minimumWorld.z);
				pResult['maximumWorld'] = new BABYLON.Vector3(this.value.maximumWorld.x, this.value.maximumWorld.y, this.value.maximumWorld.z);
			}
			
			if(this.value.minimum)
			{
				pResult['minimum'] = new BABYLON.Vector3(this.value.minimum.x, this.value.minimum.y, this.value.minimum.z);
				pResult['maximum'] = new BABYLON.Vector3(this.value.maximum.x, this.value.maximum.y, this.value.maximum.z);
			}
		}
		else if(this.value && this.value.clone)
			pResult = this.value.clone();
		else if(this.value)
			pResult = CoreUtils.clone(this.value, null, true);

		return pResult;
	}

	dispose()
	{
		if(!this.value)
			return;

		if(this.resultType === PARAM_TYPE.GEOMETRY)
		{
			for(let pMesh of this.value)
			{
				if(!pMesh.dispose)
					continue;
				Utils3D.disposeOfMaterialsAndTextures(pMesh);
				pMesh.dispose();
			}
		}
	}
}